'use client';

import type {
  CreateGiftCartItemRequest,
  ListGiftCartItemResponse,
} from '__generated__/api/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'es-toolkit';
import { useMemo } from 'react';

import { AuthMutation, AuthQueryKey, AuthQueryOptions } from '@/api';
import { useAuthStore } from '@/stores/AuthStore';

import type { CartItemData, CartProductGroup } from '../(private)/cart/_types';

// ─── Types ─────────────────────────────────────────────────────────────────

/** 지자체 정보 */
export interface GiftCartInstitution {
  id: number;
  name: string;
  logoUrl?: string;
}

interface CartData {
  institution: GiftCartInstitution | null;
  items: CartItemData[];
}

// ─── Transform Functions ───────────────────────────────────────────────────

const transformServerToLocal = (data?: ListGiftCartItemResponse): CartData => {
  if (!data?.cartItems?.length) {
    return { institution: null, items: [] };
  }

  const institution: GiftCartInstitution = {
    id: data.homeInstitutionId!,
    name: data.homeInstitutionName ?? '',
    logoUrl: data.homeInstitutionLogoUrl,
  };

  // giftCartItemOptionId 중심으로 변환
  const items: CartItemData[] = data.cartItems.flatMap((cartItem) =>
    (cartItem.options ?? []).map((option) => {
      // optionItemList가 빈 문자열이면 옵션 없음
      const hasOptions =
        !!option.optionItemList && option.optionItemList.length > 0;

      // optionItemList에서 옵션 ID 파싱 (콤마 구분)
      const optionItemIds = hasOptions
        ? option.optionItemList!.split(',').map(Number)
        : [];

      // 옵션 이름 조합 (예: "S / Red")
      const optionName = hasOptions
        ? (option.optionDetails ?? []).map((d) => d.itemName).join(' / ')
        : '';

      // 옵션 추가금 합계
      const extraPrice = hasOptions
        ? (option.optionDetails ?? []).reduce(
            (sum, d) => sum + (d.extraPrice ?? 0),
            0,
          )
        : 0;

      return {
        giftCartItemId: cartItem.giftCartItemId ?? 0,
        giftCartItemOptionId: option.giftCartItemOptionId ?? 0,
        productNumber: cartItem.productNumber ?? '',
        productName: cartItem.productName ?? '',
        imageUrl: cartItem.mainImageUrl,
        deliveryInfo: cartItem.onlineProductYN ? '온라인 상품' : '무료배송',
        requiredDonationAmount:
          cartItem.currentPrice ?? cartItem.productPrice ?? 0,
        optionName,
        extraPrice,
        optionItemIds,
        quantity: option.quantity ?? 1,
        itemTotalPrice: option.itemTotalPrice ?? 0,
        originItemTotalPrice: option.originItemTotalPrice ?? 0,
        soldOutYN: option.soldOutYN ?? cartItem.soldOutYN ?? false,
        adultYN: cartItem.adultYN,
      };
    }),
  );

  return { institution, items };
};

/** 상품별로 그룹화 (productNumber 기준) */
const groupByProductNumber = (items: CartItemData[]): CartProductGroup[] => {
  const groupMap = new Map<string, CartProductGroup>();

  items.forEach((item) => {
    const existing = groupMap.get(item.productNumber);
    if (existing) {
      existing.items.push(item);
    } else {
      groupMap.set(item.productNumber, {
        productNumber: item.productNumber,
        productName: item.productName,
        imageUrl: item.imageUrl,
        deliveryInfo: item.deliveryInfo,
        items: [item],
      });
    }
  });

  return Array.from(groupMap.values());
};

// ─── Hook ──────────────────────────────────────────────────────────────────

interface UseGiftCartOptions {
  /** staleTime (기본값: 3분) */
  staleTime?: number;
  /** gcTime (기본값: 1시간) */
  gcTime?: number;
}

export const useGiftCart = (options?: UseGiftCartOptions) => {
  const { staleTime = 1000 * 60 * 3, gcTime = 1000 * 60 * 60 } = options ?? {};

  const queryClient = useQueryClient();
  const isLogin = useAuthStore((state) => !!state.isLogin);

  const queryKey = AuthQueryKey['get:/wello/v2/auth/home/gift/cart-items']();

  // 장바구니 조회
  const cartQuery = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/home/gift/cart-items'](),
    enabled: isLogin,
    staleTime,
    gcTime,
  });

  // 수량 변경용 디바운스 invalidation (빠른 클릭 시 refetch 1회만 실행)
  const debouncedInvalidate = useMemo(
    () =>
      debounce(() => {
        queryClient.invalidateQueries({ queryKey });
      }, 300),
    [queryClient, queryKey],
  );

  // 공통 mutation 옵션
  const mutationOptions = {
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previousCart = queryClient.getQueryData(queryKey);

      return { previousCart };
    },
    onError: (
      _error: unknown,
      _variables: unknown,
      context: { previousCart: unknown } | undefined,
    ) => {
      if (context?.previousCart) {
        queryClient.setQueryData(queryKey, context.previousCart);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  };

  // 장바구니 아이템 추가
  const addMutation = useMutation({
    ...AuthMutation['post:/wello/v2/auth/home/gift/cart-items'](),
    ...mutationOptions,
  });

  // 옵션 변경
  const updateOptionsMutation = useMutation({
    ...AuthMutation[
      'put:/wello/v2/auth/home/gift/cart-items/{giftCartItemOptionId}'
    ](),
    ...mutationOptions,
  });

  // 수량 증가 (옵티미스틱 업데이트)
  const increaseMutation = useMutation({
    ...AuthMutation[
      'put:/wello/v2/auth/home/gift/cart-items/{giftCartItemOptionId}/increase'
    ](),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey });
      const previousCart = queryClient.getQueryData(queryKey);

      // 옵티미스틱 업데이트: 수량 +1
      queryClient.setQueryData(
        queryKey,
        (old: { context?: ListGiftCartItemResponse } | undefined) => {
          if (!old?.context?.cartItems) return old;

          const giftCartItemOptionId = variables.path.giftCartItemOptionId;
          const updatedCartItems = old.context.cartItems.map((cartItem) => ({
            ...cartItem,
            options: cartItem.options?.map((option) =>
              option.giftCartItemOptionId === giftCartItemOptionId
                ? { ...option, quantity: (option.quantity ?? 1) + 1 }
                : option,
            ),
          }));

          return {
            ...old,
            context: {
              ...old.context,
              cartItems: updatedCartItems,
            },
          };
        },
      );

      return { previousCart };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousCart) {
        queryClient.setQueryData(queryKey, context.previousCart);
      }
    },
    onSettled: debouncedInvalidate,
  });

  // 수량 감소 (옵티미스틱 업데이트)
  const decreaseMutation = useMutation({
    ...AuthMutation[
      'put:/wello/v2/auth/home/gift/cart-items/{giftCartItemOptionId}/decrease'
    ](),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey });
      const previousCart = queryClient.getQueryData(queryKey);

      // 옵티미스틱 업데이트: 수량 -1
      queryClient.setQueryData(
        queryKey,
        (old: { context?: ListGiftCartItemResponse } | undefined) => {
          if (!old?.context?.cartItems) return old;

          const giftCartItemOptionId = variables.path.giftCartItemOptionId;
          const updatedCartItems = old.context.cartItems.map((cartItem) => ({
            ...cartItem,
            options: cartItem.options?.map((option) =>
              option.giftCartItemOptionId === giftCartItemOptionId
                ? {
                    ...option,
                    quantity: Math.max(1, (option.quantity ?? 1) - 1),
                  }
                : option,
            ),
          }));

          return {
            ...old,
            context: {
              ...old.context,
              cartItems: updatedCartItems,
            },
          };
        },
      );

      return { previousCart };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousCart) {
        queryClient.setQueryData(queryKey, context.previousCart);
      }
    },
    onSettled: debouncedInvalidate,
  });

  // 장바구니 아이템 삭제
  const deleteMutation = useMutation({
    ...AuthMutation['delete:/wello/v2/auth/home/gift/cart-items'](),
    ...mutationOptions,
  });

  const cartData = useMemo(
    () => transformServerToLocal(cartQuery.data?.context),
    [cartQuery.data],
  );

  const productGroups = useMemo(
    () => groupByProductNumber(cartData.items),
    [cartData.items],
  );

  const addItem = async (request: CreateGiftCartItemRequest) => {
    return addMutation.mutateAsync({ body: request });
  };

  const updateOptions = async (
    giftCartItemOptionId: number,
    optionItemIds: number[],
    quantity: number,
  ) => {
    return updateOptionsMutation.mutateAsync({
      path: { giftCartItemOptionId },
      body: { optionItemIds, quantity },
    });
  };

  const increaseQuantity = async (giftCartItemOptionId: number) => {
    return increaseMutation.mutateAsync({
      path: { giftCartItemOptionId },
    });
  };

  const decreaseQuantity = async (giftCartItemOptionId: number) => {
    return decreaseMutation.mutateAsync({
      path: { giftCartItemOptionId },
    });
  };

  const deleteItems = async (giftCartItemIds: number[]) => {
    return deleteMutation.mutateAsync({ body: { giftCartItemIds } });
  };

  return {
    institution: cartData.institution,
    items: cartData.items,
    productGroups,
    totalCount: cartQuery.data?.context?.totalCount ?? 0,
    totalPrice: cartQuery.data?.context?.totalPrice ?? 0,
    isLoading: cartQuery.isLoading,
    isFetching: cartQuery.isFetching,

    addItem,
    isAdding: addMutation.isPending,

    updateOptions,
    isUpdatingOptions: updateOptionsMutation.isPending,

    increaseQuantity,
    isIncreasing: increaseMutation.isPending,

    decreaseQuantity,
    isDecreasing: decreaseMutation.isPending,

    deleteItems,
    isDeleting: deleteMutation.isPending,

    refetch: cartQuery.refetch,
  };
};
